// src/pages/CallLogs/DailerRecoring/CronJobsPanel.jsx
import React, { useCallback, useEffect, useState } from "react";
import * as cronApi from "../../../api/cronApi";
import PageLoader from "../../../components/Loader/PageLoader";
import "./CronJobs.css";

const DAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

const TZ = "Asia/Karachi";

const EMPTY_FORM = {
  name: "",
  is_enabled: true,
  timezone: TZ,
  run_time: "02:00",
  days_of_week: ["mon", "tue", "wed", "thu", "fri"],
  date_mode: "yesterday",
  date_offset_days: 1,
  server_mode: "all_active",
  server_keys: [],
  batch_size: 1000,
  max_concurrency: 2,
  retry_count: 3,
  retry_delay_sec: 60,
  starts_at: "",
  ends_at: "",
};

function formatKarachi(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-PK", {
      timeZone: TZ,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return String(iso);
  }
}

function formatDuration(started, finished) {
  if (!started) return "—";
  const a = new Date(started).getTime();
  const b = finished ? new Date(finished).getTime() : Date.now();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return "—";
  const sec = Math.floor((b - a) / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function dayChips(days = []) {
  const set = new Set((days || []).map((d) => String(d).toLowerCase()));
  return DAYS.filter((d) => set.has(d.key)).map((d) => d.label);
}

function Toast({ toast, onClose }) {
  if (!toast?.message) return null;
  const cls =
    toast.type === "success"
      ? "alert-success"
      : toast.type === "danger"
        ? "alert-danger"
        : toast.type === "warning"
          ? "alert-warning"
          : "alert-info";
  return (
    <div className={`alert ${cls} alert-dismissible fade show cron-toast d-flex align-items-center gap-2`} role="alert">
      <i
        className={`bi ${
          toast.type === "success"
            ? "bi-check-circle-fill"
            : toast.type === "danger"
              ? "bi-x-circle-fill"
              : toast.type === "warning"
                ? "bi-exclamation-triangle-fill"
                : "bi-info-circle-fill"
        }`}
      />
      <span>{toast.message}</span>
      <button type="button" className="btn-close ms-auto" onClick={onClose} />
    </div>
  );
}

function ConfirmModal({ open, title, message, confirmLabel = "Confirm", danger, busy, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="cron-modal-backdrop" role="dialog" aria-modal="true">
      <div className="cron-modal card border-0 shadow">
        <div className="card-body p-4">
          <h5 className="fw-bold mb-2">{title}</h5>
          <p className="text-muted mb-4">{message}</p>
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className={`btn btn-sm ${danger ? "btn-danger" : "btn-primary"}`}
              onClick={onConfirm}
              disabled={busy}
            >
              {busy ? <span className="spinner-border spinner-border-sm me-1" /> : null}
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CronStatusBadge({ status }) {
  const s = String(status || "pending").toLowerCase();
  return <span className={`cron-status cron-status-${s}`}>{s}</span>;
}

function LevelBadge({ level }) {
  const l = String(level || "info").toLowerCase();
  return <span className={`cron-level cron-level-${l}`}>{l}</span>;
}

function EmptyState({ icon = "bi-inbox", title, hint }) {
  return (
    <div className="cron-empty text-center py-5 text-muted">
      <i className={`bi ${icon} fs-1 d-block mb-2`} />
      <div className="fw-semibold">{title}</div>
      {hint ? <div className="small mt-1">{hint}</div> : null}
    </div>
  );
}

function LoadingBlock({ label = "Loading…" }) {
  return <PageLoader label={label} />;
}

function extractList(res) {
  if (!res) return [];
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.data?.items)) return res.data.items;
  if (Array.isArray(res.data?.rows)) return res.data.rows;
  if (Array.isArray(res.data?.schedules)) return res.data.schedules;
  if (Array.isArray(res.data?.runs)) return res.data.runs;
  if (Array.isArray(res.data?.logs)) return res.data.logs;
  if (Array.isArray(res.schedules)) return res.schedules;
  if (Array.isArray(res.runs)) return res.runs;
  if (Array.isArray(res.logs)) return res.logs;
  return [];
}

function extractTotal(res, fallbackLen) {
  return (
    res?.data?.total ??
    res?.data?.totalRecords ??
    res?.total ??
    res?.totalRecords ??
    fallbackLen
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN PANEL
═══════════════════════════════════════════════════════════ */
export default function CronJobsPanel() {
  const [view, setView] = useState("schedules"); // schedules | form | runs | run-detail | logs
  const [editId, setEditId] = useState(null);
  const [runId, setRunId] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busyKey, setBusyKey] = useState("");

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
  }, []);

  const goSchedules = () => {
    setView("schedules");
    setEditId(null);
  };
  const goCreate = () => {
    setEditId(null);
    setView("form");
  };
  const goEdit = (id) => {
    setEditId(id);
    setView("form");
  };
  const goRuns = () => setView("runs");
  const goRunDetail = (id) => {
    setRunId(id);
    setView("run-detail");
  };
  const goLogs = (presetRunId) => {
    if (presetRunId) setRunId(presetRunId);
    setView("logs");
  };

  const askConfirm = (opts) =>
    new Promise((resolve) => {
      setConfirm({
        ...opts,
        onConfirm: async () => {
          setConfirm((c) => (c ? { ...c, busy: true } : c));
          try {
            await opts.onConfirm?.();
            resolve(true);
          } catch (e) {
            showToast("danger", e.message || "Action failed");
            resolve(false);
          } finally {
            setConfirm(null);
          }
        },
        onCancel: () => {
          setConfirm(null);
          resolve(false);
        },
      });
    });

  return (
    <div className="cron-panel">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <div className="cron-subnav mb-3">
        <button
          type="button"
          className={`cron-subnav-btn ${view === "schedules" || view === "form" ? "active" : ""}`}
          onClick={goSchedules}
        >
          <i className="bi bi-calendar2-week me-1" />
          Schedules
        </button>
        <button
          type="button"
          className={`cron-subnav-btn ${view === "runs" || view === "run-detail" ? "active" : ""}`}
          onClick={goRuns}
        >
          <i className="bi bi-play-circle me-1" />
          Runs
        </button>
        <button
          type="button"
          className={`cron-subnav-btn ${view === "logs" ? "active" : ""}`}
          onClick={() => goLogs()}
        >
          <i className="bi bi-journal-text me-1" />
          Logs
        </button>
      </div>

      {view === "schedules" && (
        <SchedulesList
          showToast={showToast}
          askConfirm={askConfirm}
          busyKey={busyKey}
          setBusyKey={setBusyKey}
          onCreate={goCreate}
          onEdit={goEdit}
          onOpenRun={goRunDetail}
        />
      )}
      {view === "form" && (
        <ScheduleForm
          editId={editId}
          showToast={showToast}
          onCancel={goSchedules}
          onSaved={goSchedules}
        />
      )}
      {view === "runs" && (
        <RunsDashboard showToast={showToast} onOpenRun={goRunDetail} />
      )}
      {view === "run-detail" && (
        <RunDetail
          runId={runId}
          showToast={showToast}
          askConfirm={askConfirm}
          onBack={goRuns}
          onOpenLogs={() => goLogs(runId)}
        />
      )}
      {view === "logs" && (
        <CronLogsView showToast={showToast} initialRunId={runId} />
      )}

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        busy={confirm?.busy}
        onConfirm={confirm?.onConfirm}
        onCancel={confirm?.onCancel}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SCHEDULES LIST
═══════════════════════════════════════════════════════════ */
function SchedulesList({ showToast, askConfirm, busyKey, setBusyKey, onCreate, onEdit, onOpenRun }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await cronApi.getSchedules();
      setRows(extractList(res));
    } catch (e) {
      setRows([]);
      setError(e.message || "Failed to load schedules");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onToggle = async (row) => {
    const key = `toggle-${row.id}`;
    setBusyKey(key);
    try {
      await cronApi.toggleSchedule(row.id, !row.is_enabled);
      showToast("success", `Schedule ${!row.is_enabled ? "enabled" : "disabled"}`);
      await load();
    } catch (e) {
      showToast("danger", e.message || "Toggle failed");
    } finally {
      setBusyKey("");
    }
  };

  const onRunNow = async (row) => {
    const ok = await askConfirm({
      title: "Run schedule now?",
      message: `This will start a new run for “${row.name}”.`,
      confirmLabel: "Run Now",
      onConfirm: async () => {
        const res = await cronApi.runScheduleNow(row.id);
        const newRunId =
          res?.data?.run_id ??
          res?.data?.id ??
          res?.data?.run?.id ??
          res?.run_id ??
          res?.id;
        showToast("success", "Run started");
        if (newRunId) onOpenRun(newRunId);
        else load();
      },
    });
    if (!ok) return;
  };

  const onDelete = async (row) => {
    await askConfirm({
      title: "Delete schedule?",
      message: `Delete “${row.name}”? This cannot be undone.`,
      confirmLabel: "Delete",
      danger: true,
      onConfirm: async () => {
        await cronApi.deleteSchedule(row.id);
        showToast("success", "Schedule deleted");
        await load();
      },
    });
  };

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h5 className="mb-0 fw-bold">Cron Schedules</h5>
          <p className="text-muted small mb-0">Automate EasyPaisa recording index jobs</p>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={load} disabled={loading}>
            <i className="bi bi-arrow-clockwise me-1" />
            Refresh
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={onCreate}>
            <i className="bi bi-plus-lg me-1" />
            Create Schedule
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-warning py-2 d-flex justify-content-between align-items-center">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={load}>
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <LoadingBlock label="Loading schedules…" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="bi-calendar-x"
          title="No schedules yet"
          hint="Create a schedule to automate nightly indexing."
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="table-responsive d-none d-lg-block">
            <table className="table table-hover align-middle cron-table mb-0">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Enabled</th>
                  <th>Run time</th>
                  <th>Days</th>
                  <th>Servers</th>
                  <th>Last run</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="fw-semibold">{row.name}</div>
                      <div className="small text-muted">{row.timezone || TZ}</div>
                    </td>
                    <td>
                      <div className="form-check form-switch m-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!row.is_enabled}
                          disabled={busyKey === `toggle-${row.id}`}
                          onChange={() => onToggle(row)}
                        />
                      </div>
                    </td>
                    <td>
                      <code className="small">{row.run_time || "—"}</code>
                    </td>
                    <td>
                      <div className="cron-chips">
                        {dayChips(row.days_of_week).map((d) => (
                          <span key={d} className="cron-chip">
                            {d}
                          </span>
                        ))}
                        {!dayChips(row.days_of_week).length && <span className="text-muted">—</span>}
                      </div>
                    </td>
                    <td>
                      <span className="badge bg-light text-dark border">
                        {row.server_mode === "selected" ? "Selected" : "All active"}
                      </span>
                    </td>
                    <td>
                      {row.last_run_status ? (
                        <CronStatusBadge status={row.last_run_status} />
                      ) : (
                        <span className="text-muted small">Never</span>
                      )}
                    </td>
                    <td className="text-end">
                      <div className="btn-group btn-group-sm">
                        <button type="button" className="btn btn-outline-secondary" onClick={() => onEdit(row.id)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          disabled={busyKey === `run-${row.id}`}
                          onClick={() => onRunNow(row)}
                        >
                          Run Now
                        </button>
                        <button type="button" className="btn btn-outline-danger" onClick={() => onDelete(row)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="d-lg-none cron-card-list">
            {rows.map((row) => (
              <div key={row.id} className="cron-card">
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                  <div>
                    <div className="fw-semibold">{row.name}</div>
                    <div className="small text-muted">
                      {row.run_time} · {row.timezone || TZ}
                    </div>
                  </div>
                  <div className="form-check form-switch m-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={!!row.is_enabled}
                      disabled={busyKey === `toggle-${row.id}`}
                      onChange={() => onToggle(row)}
                    />
                  </div>
                </div>
                <div className="cron-chips mb-2">
                  {dayChips(row.days_of_week).map((d) => (
                    <span key={d} className="cron-chip">
                      {d}
                    </span>
                  ))}
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="badge bg-light text-dark border">
                    {row.server_mode === "selected" ? "Selected servers" : "All active"}
                  </span>
                  {row.last_run_status ? <CronStatusBadge status={row.last_run_status} /> : null}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onEdit(row.id)}>
                    Edit
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onRunNow(row)}>
                    Run Now
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onDelete(row)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SCHEDULE FORM
═══════════════════════════════════════════════════════════ */
function ScheduleForm({ editId, showToast, onCancel, onSaved }) {
  const isEdit = Boolean(editId);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [servers, setServers] = useState([]);
  const [serversLoading, setServersLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setServersLoading(true);
      try {
        const res = await cronApi.getActiveServers();
        setServers(extractList(res));
      } catch {
        setServers([]);
      } finally {
        setServersLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isEdit) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await cronApi.getScheduleById(editId);
        if (cancelled) return;
        const s = res?.data || res;
        setForm({
          ...EMPTY_FORM,
          name: s.name || "",
          is_enabled: s.is_enabled !== false,
          timezone: s.timezone || TZ,
          run_time: (s.run_time || "02:00").slice(0, 5),
          days_of_week: Array.isArray(s.days_of_week) ? s.days_of_week : EMPTY_FORM.days_of_week,
          date_mode: s.date_mode || "yesterday",
          date_offset_days: s.date_offset_days ?? 1,
          server_mode: s.server_mode || "all_active",
          server_keys: Array.isArray(s.server_keys) ? s.server_keys : [],
          batch_size: s.batch_size ?? 1000,
          max_concurrency: s.max_concurrency ?? 2,
          retry_count: s.retry_count ?? 3,
          retry_delay_sec: s.retry_delay_sec ?? 60,
          starts_at: s.starts_at ? String(s.starts_at).slice(0, 16) : "",
          ends_at: s.ends_at ? String(s.ends_at).slice(0, 16) : "",
        });
      } catch (e) {
        if (!cancelled) {
          showToast("danger", e.message || "Failed to load schedule");
          onCancel();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, isEdit]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: "" }));
  };

  const toggleDay = (day) => {
    setForm((f) => {
      const has = f.days_of_week.includes(day);
      return {
        ...f,
        days_of_week: has ? f.days_of_week.filter((d) => d !== day) : [...f.days_of_week, day],
      };
    });
    setErrors((e) => ({ ...e, days_of_week: "" }));
  };

  const toggleServer = (key) => {
    setForm((f) => {
      const has = f.server_keys.includes(key);
      return {
        ...f,
        server_keys: has ? f.server_keys.filter((k) => k !== key) : [...f.server_keys, key],
      };
    });
    setErrors((e) => ({ ...e, server_keys: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.run_time) e.run_time = "Run time is required";
    if (!form.days_of_week.length) e.days_of_week = "Select at least one day";
    if (form.date_mode === "custom_offset") {
      if (form.date_offset_days === "" || Number(form.date_offset_days) < 0) {
        e.date_offset_days = "Enter a valid offset (≥ 0)";
      }
    }
    if (form.server_mode === "selected" && !form.server_keys.length) {
      e.server_keys = "Select at least one server";
    }
    if (Number(form.batch_size) < 1) e.batch_size = "Must be ≥ 1";
    if (Number(form.max_concurrency) < 1) e.max_concurrency = "Must be ≥ 1";
    if (Number(form.retry_count) < 0) e.retry_count = "Must be ≥ 0";
    if (Number(form.retry_delay_sec) < 0) e.retry_delay_sec = "Must be ≥ 0";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => {
    const date_mode = form.date_mode;
    const date_offset_days =
      date_mode === "yesterday" ? 1 : date_mode === "today" ? 0 : Number(form.date_offset_days);

    return {
      name: form.name.trim(),
      is_enabled: !!form.is_enabled,
      timezone: form.timezone || TZ,
      run_time: form.run_time,
      days_of_week: form.days_of_week,
      date_mode,
      date_offset_days,
      server_mode: form.server_mode,
      server_keys: form.server_mode === "selected" ? form.server_keys : [],
      batch_size: Number(form.batch_size),
      max_concurrency: Number(form.max_concurrency),
      retry_count: Number(form.retry_count),
      retry_delay_sec: Number(form.retry_delay_sec),
      starts_at: form.starts_at || null,
      ends_at: form.ends_at || null,
    };
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showToast("warning", "Please fix the highlighted fields");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) await cronApi.updateSchedule(editId, payload);
      else await cronApi.createSchedule(payload);
      showToast("success", isEdit ? "Schedule updated" : "Schedule created");
      onSaved();
    } catch (err) {
      showToast("danger", err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingBlock label="Loading schedule…" />;

  return (
    <form onSubmit={onSubmit} className="cron-form">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h5 className="mb-0 fw-bold">{isEdit ? "Edit Schedule" : "Create Schedule"}</h5>
          <p className="text-muted small mb-0">Configure when and how indexing runs</p>
        </div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
          ← Back
        </button>
      </div>

      <div className="row g-3">
        <div className="col-md-8">
          <label className="form-label fw-semibold small">Name</label>
          <input
            className={`form-control ${errors.name ? "is-invalid" : ""}`}
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="e.g. Nightly EP Index"
          />
          <div className="invalid-feedback">{errors.name}</div>
        </div>
        <div className="col-md-4 d-flex align-items-end">
          <div className="form-check form-switch mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="cron-enabled"
              checked={form.is_enabled}
              onChange={(e) => setField("is_enabled", e.target.checked)}
            />
            <label className="form-check-label" htmlFor="cron-enabled">
              Enabled
            </label>
          </div>
        </div>

        <div className="col-md-4">
          <label className="form-label fw-semibold small">Timezone</label>
          <input
            className="form-control"
            value={form.timezone}
            onChange={(e) => setField("timezone", e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-semibold small">Run time</label>
          <input
            type="time"
            className={`form-control ${errors.run_time ? "is-invalid" : ""}`}
            value={form.run_time}
            onChange={(e) => setField("run_time", e.target.value)}
          />
          <div className="invalid-feedback">{errors.run_time}</div>
        </div>

        <div className="col-12">
          <label className="form-label fw-semibold small">Days of week</label>
          <div className="cron-day-grid">
            {DAYS.map((d) => (
              <label key={d.key} className={`cron-day ${form.days_of_week.includes(d.key) ? "on" : ""}`}>
                <input
                  type="checkbox"
                  className="d-none"
                  checked={form.days_of_week.includes(d.key)}
                  onChange={() => toggleDay(d.key)}
                />
                {d.label}
              </label>
            ))}
          </div>
          {errors.days_of_week && <div className="text-danger small mt-1">{errors.days_of_week}</div>}
        </div>

        <div className="col-12">
          <label className="form-label fw-semibold small">Date mode</label>
          <div className="d-flex flex-wrap gap-3">
            {[
              { v: "yesterday", l: "Yesterday" },
              { v: "today", l: "Today" },
              { v: "custom_offset", l: "Custom offset" },
            ].map((o) => (
              <label key={o.v} className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="date_mode"
                  checked={form.date_mode === o.v}
                  onChange={() => setField("date_mode", o.v)}
                />
                <span className="form-check-label">{o.l}</span>
              </label>
            ))}
          </div>
        </div>

        {form.date_mode === "custom_offset" && (
          <div className="col-md-4">
            <label className="form-label fw-semibold small">Offset days</label>
            <input
              type="number"
              min={0}
              className={`form-control ${errors.date_offset_days ? "is-invalid" : ""}`}
              value={form.date_offset_days}
              onChange={(e) => setField("date_offset_days", e.target.value)}
            />
            <div className="invalid-feedback">{errors.date_offset_days}</div>
            <div className="form-text">0 = today, 1 = yesterday, 2 = day before…</div>
          </div>
        )}

        <div className="col-12">
          <label className="form-label fw-semibold small">Server mode</label>
          <div className="d-flex flex-wrap gap-3 mb-2">
            {[
              { v: "all_active", l: "All active servers" },
              { v: "selected", l: "Selected servers" },
            ].map((o) => (
              <label key={o.v} className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="server_mode"
                  checked={form.server_mode === o.v}
                  onChange={() => setField("server_mode", o.v)}
                />
                <span className="form-check-label">{o.l}</span>
              </label>
            ))}
          </div>

          {form.server_mode === "selected" && (
            <div className={`cron-server-pick ${errors.server_keys ? "is-invalid-box" : ""}`}>
              {serversLoading ? (
                <div className="text-muted small">Loading servers…</div>
              ) : servers.length === 0 ? (
                <div className="text-muted small">No active servers found</div>
              ) : (
                servers.map((s) => (
                  <label key={s.server_key || s.id} className="cron-server-item">
                    <input
                      type="checkbox"
                      checked={form.server_keys.includes(s.server_key)}
                      onChange={() => toggleServer(s.server_key)}
                    />
                    <span>
                      <strong>{s.server_key}</strong>
                      <span className="text-muted ms-2 small">{s.folder}</span>
                    </span>
                  </label>
                ))
              )}
              {errors.server_keys && <div className="text-danger small mt-1">{errors.server_keys}</div>}
            </div>
          )}
        </div>

        <div className="col-6 col-md-3">
          <label className="form-label fw-semibold small">Batch size</label>
          <input
            type="number"
            min={1}
            className={`form-control ${errors.batch_size ? "is-invalid" : ""}`}
            value={form.batch_size}
            onChange={(e) => setField("batch_size", e.target.value)}
          />
        </div>
        <div className="col-6 col-md-3">
          <label className="form-label fw-semibold small">Max concurrency</label>
          <input
            type="number"
            min={1}
            className={`form-control ${errors.max_concurrency ? "is-invalid" : ""}`}
            value={form.max_concurrency}
            onChange={(e) => setField("max_concurrency", e.target.value)}
          />
        </div>
        <div className="col-6 col-md-3">
          <label className="form-label fw-semibold small">Retry count</label>
          <input
            type="number"
            min={0}
            className={`form-control ${errors.retry_count ? "is-invalid" : ""}`}
            value={form.retry_count}
            onChange={(e) => setField("retry_count", e.target.value)}
          />
        </div>
        <div className="col-6 col-md-3">
          <label className="form-label fw-semibold small">Retry delay (sec)</label>
          <input
            type="number"
            min={0}
            className={`form-control ${errors.retry_delay_sec ? "is-invalid" : ""}`}
            value={form.retry_delay_sec}
            onChange={(e) => setField("retry_delay_sec", e.target.value)}
          />
        </div>

        <div className="col-md-6">
          <label className="form-label fw-semibold small">Starts at (optional)</label>
          <input
            type="datetime-local"
            className="form-control"
            value={form.starts_at}
            onChange={(e) => setField("starts_at", e.target.value)}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label fw-semibold small">Ends at (optional)</label>
          <input
            type="datetime-local"
            className="form-control"
            value={form.ends_at}
            onChange={(e) => setField("ends_at", e.target.value)}
          />
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mt-4">
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? <span className="spinner-border spinner-border-sm me-2" /> : null}
          {isEdit ? "Save Changes" : "Create Schedule"}
        </button>
        <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/* ═══════════════════════════════════════════════════════════
   RUNS DASHBOARD
═══════════════════════════════════════════════════════════ */
function RunsDashboard({ showToast, onOpenRun }) {
  const [status, setStatus] = useState("");
  const [scheduleId, setScheduleId] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [schedules, setSchedules] = useState([]);
  const pageSize = 20;

  useEffect(() => {
    cronApi
      .getSchedules()
      .then((res) => setSchedules(extractList(res)))
      .catch(() => setSchedules([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cronApi.getRuns({
        status: status || undefined,
        schedule_id: scheduleId || undefined,
        page,
        pageSize,
        limit: pageSize,
      });
      const list = extractList(res);
      setRows(list);
      setTotal(extractTotal(res, list.length));
    } catch (e) {
      setRows([]);
      setTotal(0);
      showToast("danger", e.message || "Failed to load runs");
    } finally {
      setLoading(false);
    }
  }, [status, scheduleId, page, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h5 className="mb-0 fw-bold">Runs</h5>
          <p className="text-muted small mb-0">Recent cron execution history</p>
        </div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={load} disabled={loading}>
          <i className="bi bi-arrow-clockwise me-1" />
          Refresh
        </button>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-6 col-md-3">
          <select
            className="form-select form-select-sm"
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="">All statuses</option>
            {["pending", "running", "completed", "failed", "paused", "cancelled", "skipped"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="col-6 col-md-4">
          <select
            className="form-select form-select-sm"
            value={scheduleId}
            onChange={(e) => {
              setPage(1);
              setScheduleId(e.target.value);
            }}
          >
            <option value="">All schedules</option>
            {schedules.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <LoadingBlock />
      ) : rows.length === 0 ? (
        <EmptyState title="No runs found" hint="Trigger a schedule with Run Now to see activity here." />
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-hover align-middle cron-table mb-0">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>Schedule</th>
                  <th>Target date</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Failed</th>
                  <th>Started</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const completed = r.completed_tasks ?? r.completed ?? 0;
                  const totalTasks = r.total_tasks ?? r.total ?? 0;
                  const failed = r.failed_tasks ?? r.failed ?? 0;
                  return (
                    <tr key={r.id} className="cron-row-click" onClick={() => onOpenRun(r.id)}>
                      <td>
                        <code className="small">{r.id}</code>
                      </td>
                      <td>{r.schedule_name || r.schedule?.name || "—"}</td>
                      <td>{r.target_call_date || "—"}</td>
                      <td>
                        <CronStatusBadge status={r.status} />
                      </td>
                      <td>
                        <span className="small">
                          {completed}/{totalTasks}
                        </span>
                      </td>
                      <td className={failed ? "text-danger fw-semibold" : ""}>{failed}</td>
                      <td className="small text-muted">{formatKarachi(r.started_at)}</td>
                      <td className="small">{formatDuration(r.started_at, r.finished_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <small className="text-muted">
              Page {page} of {totalPages} · {total} runs
            </small>
            <div className="btn-group btn-group-sm">
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Prev
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   RUN DETAIL
═══════════════════════════════════════════════════════════ */
function RunDetail({ runId, showToast, askConfirm, onBack, onOpenLogs }) {
  const [run, setRun] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState("");

  const status = String(run?.status || "").toLowerCase();
  const live = status === "pending" || status === "running";

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await cronApi.getRunById(runId);
      const data = res?.data || res;
      setRun(data);
      const t =
        data.tasks ||
        data.task_list ||
        (Array.isArray(data.data) ? data.data : null) ||
        [];
      setTasks(Array.isArray(t) ? t : []);

      try {
        const logRes = await cronApi.getCronLogs({ run_id: runId, page: 1, limit: 50 });
        setLogs(extractList(logRes));
      } catch {
        setLogs([]);
      }
    } catch (e) {
      if (!silent) showToast("danger", e.message || "Failed to load run");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [runId, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!live) return undefined;
    const id = setInterval(() => load(true), 5000);
    return () => clearInterval(id);
  }, [live, load]);

  const doAction = async (kind) => {
    const map = {
      pause: { title: "Pause run?", api: cronApi.pauseRun, label: "Pause" },
      resume: { title: "Resume run?", api: cronApi.resumeRun, label: "Resume" },
      cancel: { title: "Cancel run?", api: cronApi.cancelRun, label: "Cancel", danger: true },
    };
    const cfg = map[kind];
    await askConfirm({
      title: cfg.title,
      message: `Run #${runId} will be ${kind}d.`,
      confirmLabel: cfg.label,
      danger: cfg.danger,
      onConfirm: async () => {
        setActionBusy(kind);
        try {
          await cfg.api(runId);
          showToast("success", `Run ${kind}d`);
          await load();
        } finally {
          setActionBusy("");
        }
      },
    });
  };

  if (loading) return <LoadingBlock label="Loading run…" />;
  if (!run) {
    return (
      <div>
        <button type="button" className="btn btn-outline-secondary btn-sm mb-3" onClick={onBack}>
          ← Back
        </button>
        <EmptyState title="Run not found" />
      </div>
    );
  }

  const completed = run.completed_tasks ?? run.completed ?? 0;
  const failed = run.failed_tasks ?? run.failed ?? 0;
  const pending = run.pending_tasks ?? run.pending ?? 0;
  const skipped = run.skipped_tasks ?? run.skipped ?? 0;
  const total = run.total_tasks ?? completed + failed + pending + skipped;
  const pct = total ? Math.round(((completed + skipped) / total) * 100) : 0;

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <button type="button" className="btn btn-link btn-sm px-0 mb-1" onClick={onBack}>
            ← Back to runs
          </button>
          <h5 className="mb-1 fw-bold">
            Run #{run.id}{" "}
            <CronStatusBadge status={run.status} />
            {live && <span className="cron-live ms-2">Live</span>}
          </h5>
          <div className="text-muted small">
            Target: <strong>{run.target_call_date || "—"}</strong>
            {" · "}
            Started {formatKarachi(run.started_at)}
            {run.finished_at ? ` · Finished ${formatKarachi(run.finished_at)}` : ""}
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {status === "running" && (
            <button
              type="button"
              className="btn btn-warning btn-sm"
              disabled={!!actionBusy}
              onClick={() => doAction("pause")}
            >
              Pause
            </button>
          )}
          {status === "paused" && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!!actionBusy}
              onClick={() => doAction("resume")}
            >
              Resume
            </button>
          )}
          {(status === "running" || status === "pending" || status === "paused") && (
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              disabled={!!actionBusy}
              onClick={() => doAction("cancel")}
            >
              Cancel
            </button>
          )}
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onOpenLogs}>
            View logs
          </button>
        </div>
      </div>

      <div className="cron-progress-card mb-4">
        <div className="d-flex justify-content-between mb-2">
          <span className="fw-semibold small">Progress</span>
          <span className="small text-muted">{pct}%</span>
        </div>
        <div className="progress mb-3" style={{ height: 10 }}>
          <div className="progress-bar bg-success" style={{ width: `${total ? (completed / total) * 100 : 0}%` }} />
          <div className="progress-bar bg-danger" style={{ width: `${total ? (failed / total) * 100 : 0}%` }} />
          <div className="progress-bar bg-secondary" style={{ width: `${total ? (skipped / total) * 100 : 0}%` }} />
        </div>
        <div className="row g-2 text-center small">
          <div className="col-3">
            <div className="fw-bold text-success">{completed}</div>
            <div className="text-muted">Completed</div>
          </div>
          <div className="col-3">
            <div className="fw-bold text-danger">{failed}</div>
            <div className="text-muted">Failed</div>
          </div>
          <div className="col-3">
            <div className="fw-bold text-secondary">{pending}</div>
            <div className="text-muted">Pending</div>
          </div>
          <div className="col-3">
            <div className="fw-bold" style={{ color: "#7c3aed" }}>
              {skipped}
            </div>
            <div className="text-muted">Skipped</div>
          </div>
        </div>
      </div>

      <h6 className="fw-bold mb-2">Tasks</h6>
      {tasks.length === 0 ? (
        <EmptyState title="No tasks yet" hint={live ? "Tasks will appear as the run progresses." : undefined} />
      ) : (
        <div className="table-responsive mb-4">
          <table className="table table-sm table-bordered align-middle cron-table mb-0">
            <thead className="table-light">
              <tr>
                <th>Server</th>
                <th>Call date</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>Inserted</th>
                <th>Skipped</th>
                <th>Total</th>
                <th>Job ID</th>
                <th>Last error</th>
                <th>Times</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t, i) => (
                <tr key={t.id ?? i}>
                  <td>
                    <code className="small">{t.server_key}</code>
                  </td>
                  <td>{t.call_date || "—"}</td>
                  <td>
                    <CronStatusBadge status={t.status} />
                  </td>
                  <td>{t.attempts ?? "—"}</td>
                  <td>{t.inserted ?? "—"}</td>
                  <td>{t.skipped ?? "—"}</td>
                  <td>{t.total_records ?? "—"}</td>
                  <td>
                    {t.index_job_id ? (
                      <code className="small">{t.index_job_id}</code>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="small text-danger" style={{ maxWidth: 180 }}>
                    {t.last_error || "—"}
                  </td>
                  <td className="small text-muted">
                    {formatKarachi(t.started_at)}
                    {t.finished_at ? (
                      <>
                        <br />→ {formatKarachi(t.finished_at)}
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h6 className="fw-bold mb-2">Recent logs</h6>
      {logs.length === 0 ? (
        <p className="text-muted small">No logs for this run yet.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Time</th>
                <th>Level</th>
                <th>Event</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {logs.slice(0, 20).map((l, i) => (
                <tr key={l.id ?? i}>
                  <td className="small text-muted">{formatKarachi(l.created_at)}</td>
                  <td>
                    <LevelBadge level={l.level} />
                  </td>
                  <td className="small">{l.event || "—"}</td>
                  <td className="small">{l.message || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CRON LOGS
═══════════════════════════════════════════════════════════ */
function CronLogsView({ showToast, initialRunId }) {
  const [runId, setRunId] = useState(initialRunId ? String(initialRunId) : "");
  const [level, setLevel] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState({});
  const pageSize = 25;

  useEffect(() => {
    if (initialRunId) setRunId(String(initialRunId));
  }, [initialRunId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cronApi.getCronLogs({
        run_id: runId || undefined,
        level: level || undefined,
        page,
        pageSize,
        limit: pageSize,
      });
      const list = extractList(res);
      setRows(list);
      setTotal(extractTotal(res, list.length));
    } catch (e) {
      setRows([]);
      setTotal(0);
      showToast("danger", e.message || "Failed to load logs");
    } finally {
      setLoading(false);
    }
  }, [runId, level, page, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h5 className="mb-0 fw-bold">Cron Logs</h5>
          <p className="text-muted small mb-0">Operational log stream</p>
        </div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={load} disabled={loading}>
          <i className="bi bi-arrow-clockwise me-1" />
          Refresh
        </button>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-6 col-md-3">
          <input
            className="form-control form-control-sm"
            placeholder="Filter run_id"
            value={runId}
            onChange={(e) => {
              setPage(1);
              setRunId(e.target.value);
            }}
          />
        </div>
        <div className="col-6 col-md-3">
          <select
            className="form-select form-select-sm"
            value={level}
            onChange={(e) => {
              setPage(1);
              setLevel(e.target.value);
            }}
          >
            <option value="">All levels</option>
            <option value="info">info</option>
            <option value="warn">warn</option>
            <option value="error">error</option>
          </select>
        </div>
      </div>

      {loading ? (
        <LoadingBlock />
      ) : rows.length === 0 ? (
        <EmptyState title="No logs" hint="Logs appear when schedules run." />
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle cron-table mb-0">
              <thead className="table-light">
                <tr>
                  <th />
                  <th>Time</th>
                  <th>Level</th>
                  <th>Event</th>
                  <th>Message</th>
                  <th>Run</th>
                  <th>Task</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((l, i) => {
                  const key = l.id ?? i;
                  const hasMeta = l.meta != null && l.meta !== "";
                  return (
                    <React.Fragment key={key}>
                      <tr>
                        <td>
                          {hasMeta && (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0"
                              onClick={() =>
                                setExpanded((ex) => ({ ...ex, [key]: !ex[key] }))
                              }
                            >
                              <i className={`bi bi-chevron-${expanded[key] ? "up" : "down"}`} />
                            </button>
                          )}
                        </td>
                        <td className="small text-muted text-nowrap">{formatKarachi(l.created_at)}</td>
                        <td>
                          <LevelBadge level={l.level} />
                        </td>
                        <td className="small">{l.event || "—"}</td>
                        <td className="small">{l.message || "—"}</td>
                        <td>
                          <code className="small">{l.run_id ?? "—"}</code>
                        </td>
                        <td>
                          <code className="small">{l.task_id ?? "—"}</code>
                        </td>
                      </tr>
                      {expanded[key] && hasMeta && (
                        <tr>
                          <td colSpan={7}>
                            <pre className="cron-meta mb-0">
                              {typeof l.meta === "string"
                                ? l.meta
                                : JSON.stringify(l.meta, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <small className="text-muted">
              Page {page} of {totalPages}
            </small>
            <div className="btn-group btn-group-sm">
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Prev
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
